/**
 * Ozon unit economics, ruleset effective from 01.07.2026.
 *
 * Formula structure comes from the four calculator sheets in
 * "Unit-экономика OZON c 01.07.26.xlsx" (FBO/FBS, up to/over 300 RUB).
 * Mutable category and route tariffs remain inputs because Ozon changes them
 * independently and they will later be filled from the Seller API.
 *
 * Intentional corrections to the workbook:
 * - failed orders are normalized to one successfully sold item;
 * - FBO acceptance is an explicit RUB/item input instead of accidentally using
 *   the advertising-rate cell as an additional-liter tariff;
 * - the two price sheets are represented by one price-tier selector.
 */

export const OZON_2026_RULESET = {
  id: "ozon-2026-07-v1",
  label: "Ozon с 01.07.2026 · версия 1",
  source: "Unit-экономика OZON c 01.07.26.xlsx",
  returnTripMultiplier: 2,
};

export const OZON_TAX_MODES = [
  { value: "usn6", label: "УСН Доходы 6% · без НДС" },
  { value: "usn6_vat5", label: "УСН Доходы 6% · НДС 5%" },
  { value: "usn6_vat7", label: "УСН Доходы 6% · НДС 7%" },
  { value: "ausn8", label: "АУСН Доходы 8% · без НДС" },
  { value: "ausn8_vat5", label: "АУСН Доходы 8% · НДС 5%" },
  { value: "ausn8_vat7", label: "АУСН Доходы 8% · НДС 7%" },
];

const COMMON_DEFAULTS = {
  price: 0,
  packaging: 0,
  overhead: 0,
  widthCm: 10,
  lengthCm: 6,
  heightCm: 11,
  commissionPercent: 0,
  commissionUpTo100Percent: 0,
  commissionUpTo300Percent: 0,
  commissionOver300Percent: 0,
  deliveryTariff: 0,
  lastMileTariff: 25,
  returnTariff: 0,
  storagePerUnit: 0,
  advertisingPercent: 0,
  taxMode: "usn6",
};

export const OZON_2026_DEFAULT_INPUTS = {
  fbo: {
    ...COMMON_DEFAULTS,
    buyoutPercent: 85,
    acquiringPercent: 2.5,
    nonLocalPercent: 0,
    acceptancePerUnit: 5,
    processingPerOrder: 0,
  },
  fbs: {
    ...COMMON_DEFAULTS,
    buyoutPercent: 65,
    acquiringPercent: 2,
    nonLocalPercent: 0,
    acceptancePerUnit: 0,
    processingPerOrder: 30,
  },
};

const numberOr = (value, fallback = 0) => {
  const parsed = typeof value === "string" ? Number(value.replace(",", ".")) : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nonNegative = (value, fallback = 0) => Math.max(0, numberOr(value, fallback));
const percent = (value, fallback = 0) => Math.min(100, nonNegative(value, fallback));
// Per-sale economics is undefined at 0% buyout. The UI rejects zero; this
// lower bound also keeps legacy records conservative instead of hiding costs.
const buyoutPercent = (value, fallback) =>
  Math.min(100, Math.max(1, numberOr(value, fallback)));
const divide = (value, divisor) => (divisor ? value / divisor : 0);

export function normalizeOzon2026Inputs(scheme, value = {}) {
  const normalizedScheme = scheme === "fbo" ? "fbo" : "fbs";
  const defaults = OZON_2026_DEFAULT_INPUTS[normalizedScheme];
  const price = nonNegative(value.price, defaults.price);
  const legacyCommissionPercent = price <= 100
    ? value.commissionUpTo100Percent
    : price <= 300
      ? value.commissionUpTo300Percent
      : value.commissionOver300Percent;
  return {
    price,
    packaging: nonNegative(value.packaging, defaults.packaging),
    overhead: nonNegative(value.overhead, defaults.overhead),
    widthCm: nonNegative(value.widthCm, defaults.widthCm),
    lengthCm: nonNegative(value.lengthCm, defaults.lengthCm),
    heightCm: nonNegative(value.heightCm, defaults.heightCm),
    buyoutPercent: buyoutPercent(value.buyoutPercent, defaults.buyoutPercent),
    acquiringPercent: percent(value.acquiringPercent, defaults.acquiringPercent),
    // The UI asks for only the commission that applies to the product now.
    // Legacy tier fields remain readable for already saved records.
    commissionPercent: value.commissionPercent == null
      ? percent(legacyCommissionPercent, defaults.commissionPercent)
      : percent(value.commissionPercent, defaults.commissionPercent),
    commissionUpTo100Percent: percent(
      value.commissionUpTo100Percent,
      defaults.commissionUpTo100Percent,
    ),
    commissionUpTo300Percent: percent(
      value.commissionUpTo300Percent,
      defaults.commissionUpTo300Percent,
    ),
    commissionOver300Percent: percent(
      value.commissionOver300Percent,
      defaults.commissionOver300Percent,
    ),
    deliveryTariff: nonNegative(value.deliveryTariff, defaults.deliveryTariff),
    lastMileTariff: nonNegative(value.lastMileTariff, defaults.lastMileTariff),
    returnTariff: nonNegative(value.returnTariff, defaults.returnTariff),
    processingPerOrder: nonNegative(value.processingPerOrder, defaults.processingPerOrder),
    storagePerUnit: nonNegative(value.storagePerUnit, defaults.storagePerUnit),
    acceptancePerUnit: nonNegative(value.acceptancePerUnit, defaults.acceptancePerUnit),
    nonLocalPercent: percent(value.nonLocalPercent, defaults.nonLocalPercent),
    advertisingPercent: percent(value.advertisingPercent, defaults.advertisingPercent),
    taxMode: OZON_TAX_MODES.some((mode) => mode.value === value.taxMode)
      ? value.taxMode
      : defaults.taxMode,
  };
}

function categoryCommissionPercent(inputs) {
  return inputs.commissionPercent;
}

function taxRates(mode) {
  return {
    income: mode.startsWith("ausn8") ? 0.08 : 0.06,
    vat: mode.endsWith("vat5") ? 0.05 : mode.endsWith("vat7") ? 0.07 : 0,
  };
}

export function calculateOzon2026(scheme, rawInputs = {}, rawCostPrice = 0) {
  const normalizedScheme = scheme === "fbo" ? "fbo" : "fbs";
  const inputs = normalizeOzon2026Inputs(normalizedScheme, rawInputs);
  const costPrice = nonNegative(rawCostPrice);
  // costPrice is the complete unit cost and already includes packaging.
  // Keep the legacy packaging input in normalized records for compatibility,
  // but do not add it again to the calculation.
  const totalCost = costPrice + inputs.overhead;
  const volumeLiters = (inputs.widthCm * inputs.lengthCm * inputs.heightCm) / 1000;
  const buyoutRate = inputs.buyoutPercent / 100;
  const failedOrdersPerSale = buyoutRate ? (1 - buyoutRate) / buyoutRate : 0;
  const outboundLogistics = inputs.deliveryTariff + inputs.lastMileTariff;

  // Ozon shows outbound logistics and "Return or cancellation / Reverse
  // logistics" as separate tariffs. A failed order has its outbound trip plus
  // the explicit reverse tariff; it must not be approximated as two identical
  // trips because current route ranges differ (for example 67–154 vs 67–81).
  // When no reverse tariff was entered, preserve the workbook approximation.
  const reverseLogisticsTariff = inputs.returnTariff || outboundLogistics;
  const failedOrderLogistics =
    failedOrdersPerSale * (outboundLogistics + reverseLogisticsTariff);
  const logistics = buyoutRate ? outboundLogistics + failedOrderLogistics : 0;

  // FBS order processing is charged for every handed-over order, including the
  // expected failed orders. FBO acceptance is charged once per inventory unit.
  const processing = buyoutRate ? inputs.processingPerOrder / buyoutRate : 0;
  const acceptance = inputs.acceptancePerUnit;
  const commissionRate = categoryCommissionPercent(inputs) / 100;
  const commission = inputs.price * commissionRate;
  const acquiring = inputs.price * (inputs.acquiringPercent / 100);
  const nonLocalSurcharge =
    normalizedScheme === "fbo" ? inputs.price * (inputs.nonLocalPercent / 100) : 0;
  const advertisingSpend = inputs.price * (inputs.advertisingPercent / 100);
  const { income: incomeTaxRate, vat: vatRate } = taxRates(inputs.taxMode);
  const vat = inputs.price * vatRate;
  const incomeTax = (inputs.price - vat) * incomeTaxRate;
  const tax = vat + incomeTax;

  const marketplaceExpenses =
    commission +
    acquiring +
    nonLocalSurcharge +
    logistics +
    processing +
    acceptance +
    inputs.storagePerUnit +
    tax;
  const payoutBeforeTax =
    inputs.price -
    commission -
    acquiring -
    nonLocalSurcharge -
    logistics -
    processing -
    acceptance -
    inputs.storagePerUnit;
  const profit = inputs.price - marketplaceExpenses - totalCost;
  const profitAfterAdvertising = profit - advertisingSpend;

  return {
    rulesetId: OZON_2026_RULESET.id,
    scheme: normalizedScheme,
    totalCost,
    volumeLiters,
    buyoutRate,
    failedOrdersPerSale,
    commissionRate,
    commission,
    acquiring,
    nonLocalSurcharge,
    outboundLogistics,
    reverseLogisticsTariff,
    failedOrderLogistics,
    logistics,
    processing,
    acceptance,
    storage: inputs.storagePerUnit,
    vat,
    incomeTax,
    tax,
    marketplaceExpenses,
    payoutBeforeTax,
    netRevenue: payoutBeforeTax - tax,
    profit,
    margin: divide(profit, inputs.price),
    profitability: divide(profitAfterAdvertising, totalCost),
    advertisingSpend,
    profitAfterAdvertising,
    marginAfterAdvertising: divide(profitAfterAdvertising, inputs.price),
  };
}
